import { getLeadDisplayName } from './leadDisplayName';

interface LeadCardFieldsInput {
  company?: string | null;
  fullName?: string | null;
  email?: string | null;
  customFields?: Record<string, unknown> | null;
}

function asTrimmedString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/**
 * Resolves the secondary line for a lead card: the company, shown only when
 * it adds information beyond the card title. The title itself already
 * prioritizes the contact person (FLOSEAI-531: customFields.contactName >
 * fullName > company > email), so this surfaces whichever of company/fullName
 * still differs from it — including the legacy shape where manual creation
 * stored the company in `fullName` and the person in `customFields.contactName`.
 */
export function getLeadCardContactName(
  lead: LeadCardFieldsInput | null | undefined,
): string | undefined {
  if (!lead) return undefined;

  const title = getLeadDisplayName(lead);
  const normalizedTitle = title?.toLowerCase();
  const company = asTrimmedString(lead.company);
  const fullName = asTrimmedString(lead.fullName);

  return [company, fullName].find(
    (candidate) => candidate && candidate.toLowerCase() !== normalizedTitle,
  );
}

/**
 * The lead description ("what is being sold to this lead", FLOSEAI-550) is
 * persisted under `customFields.productInterest`. Returns undefined for
 * missing, empty, or non-string values so cards can omit the line entirely.
 */
export function getLeadCardDescription(
  lead: Pick<LeadCardFieldsInput, 'customFields'> | null | undefined,
): string | undefined {
  return asTrimmedString(lead?.customFields?.productInterest);
}
